import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { style } from './add-to-order-templates.style.js'
import { OrderTemplates } from 'components/discovery/product/add-to-order-templates/typing/order-templates-types.js'
import { ORDER_TEMPLATES_STORE, type OrderTemplatesStore } from 'components/discovery/shared/order-templates/stores/order-templates-store.js'
import { InternalProduct } from 'components/discovery/product/tile-parts/tile-parts-typing.js'
import { track } from 'components/shared/analytics/analytics.js'
import type { AnalyticsContext } from 'components/shared/analytics/analytics-types.js'
import { OrderTemplatesLoadingMixin } from 'components/shared/order-templates/order-templates-loading-mixin.js'
import { HeadingSlotMixin } from 'components/shared/heading/heading-slot-mixin.js'
import { PDP_PRODUCT_STORE, type PdpProductStore } from 'components/discovery/pdp/pdp-product/pdp-product-store.js'
import { orderTemplateQuantity } from 'components/discovery/product/add-to-order-templates/order-template-quantity.js'


@customElement('csc-add-to-order-templates')
export class CSCAddToOrderTemplates extends HeadingSlotMixin(OrderTemplatesLoadingMixin(LitElement)) {
  static override styles: CSSResultGroup = [hostStyle, style]

  @inject(ORDER_TEMPLATES_STORE)
  private store!: OrderTemplatesStore

  @inject(PDP_PRODUCT_STORE)
  private pdpProductStore!: PdpProductStore

  @property({ type: Number, attribute: 'product-id' })
  productId = 0

  @property({ type: Number })
  quantity = 0

  @property({ attribute: false })
  trackingProduct: InternalProduct | null = null

  /** @deprecated Use `trackingProduct` instead: the product is identified by `product-id`, this object is only sent to analytics. */
  @property({ type: Object })
  set product(value: InternalProduct | null) {
    console.warn('<csc-add-to-order-templates> "product" is deprecated, use the "trackingProduct" property instead: the product is identified by "product-id".')
    this.trackingProduct = value
  }

  get product() {
    return this.trackingProduct
  }

  @property({ attribute: false })
  trackingContext: AnalyticsContext = {}

  @state()
  private items: OrderTemplates = { total_found: 0, templates: [] }

  @property({ type: Number, attribute: 'display-limit' })
  displayLimit = 3

  @state()
  private refetching = false

  @state()
  private selectedIds = new Set<number>()

  @state()
  private loadingIds = new Set<number>()

  @state()
  private loadFailed = false

  @state()
  private writeFailed = false

  constructor() {
    super()
    createEffect(() => {
      this.loadFailed = this.store.loadError() !== null
    })
    createEffect(() => {
      this.refetching = this.store.refetching()
    })
    createEffect(() => {
      const templates = this.store.orderTemplates()
      if (templates) {
        const orderTemplates = templates as OrderTemplates
        this.items = orderTemplates
        this.selectedIds = new Set(
          orderTemplates.templates
            .filter(t => t.products?.some(p => p.id === this.productId))
            .map(t => t.id)
        )
      }
    })
  }

  private handleSelectableClick(id: number) {
    if (this.loadingIds.has(id)) return
    void this.toggleTemplate(id)
  }

  private async toggleTemplate(id: number) {
    const previous = this.selectedIds
    const updated = new Set(previous)
    const isAdding = !updated.has(id)
    isAdding ? updated.add(id) : updated.delete(id)
    this.selectedIds = updated
    this.loadingIds = new Set(this.loadingIds).add(id)
    this.writeFailed = false
    try {
      if (isAdding) {
        await this.store.addProductToTemplate(id, this.productId, orderTemplateQuantity(this.quantity, this.productId, this.pdpProductStore.product()))
        if (!this.isConnected) return
        this.trackAddedToWishlist()
      } else {
        await this.store.removeProductFromTemplate(id, this.productId)
      }
    } catch (error) {
      console.error('Failed to update order template', error)
      if (!this.isConnected) return
      this.selectedIds = previous
      this.writeFailed = true
    } finally {
      if (this.isConnected) this.clearLoading(id)
    }
  }

  private clearLoading(id: number) {
    const updatedLoading = new Set(this.loadingIds)
    updatedLoading.delete(id)
    this.loadingIds = updatedLoading
  }

  private trackAddedToWishlist() {
    if (!this.trackingProduct) return
    track({ type: 'product-added-to-wishlist', product: this.trackingProduct, context: this.trackingContext })
  }

  private get showAllLink() {
    return !this.refetching && !this.loadFailed && Number.isFinite(this.displayLimit)
  }

  override render() {
    return html`
      ${this.renderHeadingSlot()}
      ${this.loadFailed ? html`
        <cse-alert variant="error">
          ${t('error_load_templates', { ns: 'csc-add-to-order-template', default: 'Your order templates could not be loaded. Please try again later.' })}
        </cse-alert>
      ` : nothing}
      ${this.writeFailed ? html`
        <cse-alert variant="error">
          ${t('error_update_template', { ns: 'csc-add-to-order-template', default: 'Your order template could not be updated. Please try again.' })}
        </cse-alert>
      ` : nothing}
      <cse-list>
        ${!this.refetching && this.items.templates.length > 0 ? this.items.templates.slice(0, this.displayLimit).map(item => html`
          <cse-list-item>
            <cse-selectable
              class="item"
              fit="fill"
              state="${this.loadingIds.has(item.id) ? 'loading' : this.selectedIds.has(item.id) ? 'selected' : 'selectable'}"
              @click="${() => this.handleSelectableClick(item.id)}"
            >
              <div class="checkbox-wrapper">
                <cse-checkbox
                  ?checked="${this.selectedIds.has(item.id)}"
                  ?disabled="${this.loadingIds.has(item.id)}"
                >
                  ${item.name}
                </cse-checkbox>
              </div>
            </cse-selectable>
          </cse-list-item>
        `) : html`
          <cse-list-item>
            <cse-skeleton height="64" width="350" index="0" variant="secondary"></cse-skeleton>
          </cse-list-item>
          <cse-list-item>
            <cse-skeleton height="64" width="350" index="1" variant="secondary"></cse-skeleton>
          </cse-list-item>
          <cse-list-item>
            <cse-skeleton height="64" width="350" index="2" variant="secondary"></cse-skeleton>
          </cse-list-item>
        `}
      </cse-list>
      ${this.showAllLink ? html`
        <csc-order-template-slide-in
          product-id="${this.productId}"
          quantity="${this.quantity}"
          .trackingProduct="${this.trackingProduct}"
          .trackingContext="${this.trackingContext}"
        >
          <cse-link slot="trigger">${t('show_more', { ns: 'csc-add-to-order-template', default: 'Show all order templates' })}</cse-link>
        </csc-order-template-slide-in>
      ` : nothing}
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-add-to-order-templates': CSCAddToOrderTemplates
  }
}
